/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { graphql, useStaticQuery } from 'gatsby';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { CookieBannerProps } from '../components/CookieBanner';
import ModalOverlay from '../components/ModalOverlay';
import TabbedCardList, { Tab } from '../components/TabbedCardList';
import DangerouslySetHtmlContent from '../components/lib/DangerouslySetHtmlContent';
import {
  getCookieValue,
  removeCookie,
  setCookie,
  signAndObfuscate,
  verifyAndDeobfuscate,
} from '../utils/utils';

const COOKIE_NAME = 'cookieSettings';
const COOKIE_BANNER_DISMISSED_KEY = 'cookieBarDismissed';
const COOKIE_SIGNING_SECRET = 'Yk5FbOX5ElL';
const CONSENT_MODE_STORAGE_KEY = 'consentMode';

type ConsentValue = 'granted' | 'denied';

interface ConsentMode {
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
  analytics_storage: ConsentValue;
  functionality_storage: ConsentValue;
  personalization_storage: ConsentValue;
  security_storage: ConsentValue;
}

function updateGTMConsent(granted: boolean) {
  const value: ConsentValue = granted ? 'granted' : 'denied';
  const consentMode: ConsentMode = {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
    functionality_storage: value,
    personalization_storage: value,
    security_storage: value,
  };

  // Update GTM consent
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', consentMode);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'consent_updated' });
  }

  // Persist to localStorage for GTM script to pick up on next page load
  window.localStorage.setItem(CONSENT_MODE_STORAGE_KEY, JSON.stringify(consentMode));
}

const ModalContainer = styled.div`
  max-width: 1170px;
  width: 100%;
`;

interface CookieSettingsCookieValue {
  nonEssentialCookiesAccepted: boolean;
  version?: number;
}

interface QueryData {
  sanityWebsite: {
    cookieBar: CookieBannerProps;
  };
}

interface CookieSettingsContextValue {
  isCookieBannerOpen: boolean;
  closeCookieBanner: () => void;
  acceptAllCookies: () => void;
  showCookieSettingsModal: () => void;
}

// We assume this CookieSettingsContext will never be used unless inside
// the CookieSettingsContext.Provider, and so the default is never used.
const CookieSettingsContext = createContext<CookieSettingsContextValue>(
  {} as CookieSettingsContextValue,
);

export const useCookieSettings = () => useContext(CookieSettingsContext);

interface CookieSettingsProviderProps {
  children: React.ReactNode;
}

export function CookieSettingsProvider({ children }: CookieSettingsProviderProps) {
  const queryData: QueryData = useStaticQuery(graphql`
    {
      sanityWebsite {
        cookieBar {
          _rawBannerText(resolveReferences: { maxDepth: 6 })
          versionNumber
          acceptButtonText
          settingsButtonText
          cookieSettingsModal {
            privacyOverview {
              tabLabel
              _rawTabContent(resolveReferences: { maxDepth: 6 })
            }
            essentialCookies {
              tabLabel
              _rawTabContent(resolveReferences: { maxDepth: 6 })
            }
            nonEssentialCookies {
              tabLabel
              _rawTabContent(resolveReferences: { maxDepth: 6 })
            }
          }
          nonEssentialTrackingCode {
            head
            body
            footer
          }
        }
      }
    }
  `);

  const { cookieSettingsModal, nonEssentialTrackingCode, versionNumber } =
    queryData.sanityWebsite.cookieBar;

  const [nonEssentialCookiesEnabled, setNonEssentialCookiesEnabled] = useState(false);
  const [isCookieBannerOpen, setIsCookieBannerOpen] = useState(false);
  const [isCookieSettingsModalOpen, setIsCookieSettingsModalOpen] = useState(false);
  const headScriptsContainer = useRef<HTMLDivElement | null>(null);
  const bodyScriptsContainer = useRef<HTMLDivElement | null>(null);
  const footerScriptsContainer = useRef<HTMLDivElement | null>(null);

  function showCookieSettingsModal() {
    setIsCookieSettingsModalOpen(true);
  }

  function closeCookieBanner() {
    setIsCookieBannerOpen(false);
    window.sessionStorage.setItem(COOKIE_BANNER_DISMISSED_KEY, 'true');
  }

  function acceptAllCookies() {
    setIsCookieBannerOpen(false);
    storeCookieSettingsChoices({ nonEssentialCookiesAccepted: true });
  }

  function activateNonEssentialCookiesScripts() {
    headScriptsContainer.current = document.querySelector('#headScriptsContainer');
    bodyScriptsContainer.current = document.querySelector('#bodyScriptsContainer');
    footerScriptsContainer.current = document.querySelector('#footerScriptsContainer');
    setNonEssentialCookiesEnabled(true);
  }

  useEffect(() => {
    const cookieSettingsValue = verifyAndDeobfuscate(
      getCookieValue(COOKIE_NAME),
      COOKIE_SIGNING_SECRET,
    );
    if (cookieSettingsValue) {
      const cookieSettings: CookieSettingsCookieValue = JSON.parse(cookieSettingsValue);
      if (cookieSettings.version && versionNumber && cookieSettings.version !== versionNumber) {
        removeCookie(COOKIE_NAME);
        setIsCookieBannerOpen(true);
      } else {
        if (cookieSettings.nonEssentialCookiesAccepted) {
          activateNonEssentialCookiesScripts();
        }
      }
    } else {
      removeCookie(COOKIE_NAME);
      const cookieBarDismissed = window.sessionStorage.getItem(COOKIE_BANNER_DISMISSED_KEY);
      if (!cookieBarDismissed) {
        setIsCookieBannerOpen(true);
      }
    }
  }, []);

  function storeCookieSettingsChoices({
    nonEssentialCookiesAccepted,
  }: {
    nonEssentialCookiesAccepted: boolean;
  }) {
    setCookie(
      COOKIE_NAME,
      signAndObfuscate(
        JSON.stringify({
          nonEssentialCookiesAccepted,
          version: versionNumber,
        } satisfies CookieSettingsCookieValue),
        COOKIE_SIGNING_SECRET,
      ),
      { expires: 3650, sameSite: 'strict' },
    );

    updateGTMConsent(nonEssentialCookiesAccepted);

    if (nonEssentialCookiesEnabled && !nonEssentialCookiesAccepted) {
      // Reload window so it loads without non essential scripts
      window.location.reload();
    } else if (!nonEssentialCookiesEnabled && nonEssentialCookiesAccepted) {
      activateNonEssentialCookiesScripts();
    }
  }

  const tabs: Array<Tab> = [
    {
      title: cookieSettingsModal.privacyOverview.tabLabel || 'Privacy Overview',
      text: cookieSettingsModal.privacyOverview._rawTabContent,
    },
    {
      title: cookieSettingsModal.essentialCookies.tabLabel || 'Essential Cookies',
      text: cookieSettingsModal.essentialCookies._rawTabContent,
      toggleButton: {
        id: 'essentialCookies',
        initialState: true,
        disabled: true,
      },
    },
    {
      title: cookieSettingsModal.nonEssentialCookies.tabLabel || 'Non-Essential Cookies',
      text: cookieSettingsModal.nonEssentialCookies._rawTabContent,
      toggleButton: {
        id: 'nonEssentialCookies',
        initialState: nonEssentialCookiesEnabled,
      },
    },
  ];

  return (
    <CookieSettingsContext.Provider
      value={{
        isCookieBannerOpen,
        closeCookieBanner,
        acceptAllCookies,
        showCookieSettingsModal,
      }}
    >
      <ModalOverlay
        open={isCookieSettingsModalOpen}
        onClose={() => {
          setIsCookieSettingsModalOpen(false);
        }}
      >
        <ModalContainer>
          <TabbedCardList
            key={String(isCookieSettingsModalOpen)}
            tabs={tabs}
            buttons={[
              {
                title: 'Enable all',
                checkEnabled: togglesStates => !togglesStates.nonEssentialCookies,
                onButtonClick: () => {
                  storeCookieSettingsChoices({ nonEssentialCookiesAccepted: true });
                  setIsCookieSettingsModalOpen(false);
                  setIsCookieBannerOpen(false);
                },
              },
              {
                title: 'Save changes',
                checkEnabled: togglesStates =>
                  togglesStates.nonEssentialCookies !== nonEssentialCookiesEnabled,
                onButtonClick: togglesStates => {
                  storeCookieSettingsChoices({
                    nonEssentialCookiesAccepted: togglesStates.nonEssentialCookies,
                  });
                  setIsCookieSettingsModalOpen(false);
                  setIsCookieBannerOpen(false);
                },
              },
            ]}
          />
        </ModalContainer>
      </ModalOverlay>
      {nonEssentialCookiesEnabled && nonEssentialTrackingCode && (
        <>
          {nonEssentialTrackingCode.head &&
            createPortal(
              <DangerouslySetHtmlContent
                style={{ display: 'none' }}
                html={nonEssentialTrackingCode.head}
              ></DangerouslySetHtmlContent>,
              headScriptsContainer.current!,
            )}
          {nonEssentialTrackingCode.body &&
            createPortal(
              <DangerouslySetHtmlContent
                style={{ display: 'none' }}
                html={nonEssentialTrackingCode.body}
              ></DangerouslySetHtmlContent>,
              bodyScriptsContainer.current!,
            )}
          {nonEssentialTrackingCode.footer &&
            createPortal(
              <DangerouslySetHtmlContent
                style={{ display: 'none' }}
                html={nonEssentialTrackingCode.footer}
              ></DangerouslySetHtmlContent>,
              footerScriptsContainer.current!,
            )}
        </>
      )}
      {children}
    </CookieSettingsContext.Provider>
  );
}
